<template>
  <div id="person-details-page">
    <div class="btn btn__floating btn__round__large btn__round__large"
          v-if="facilitiesAdmin.length">
      <va-popover
        :message="$t('clinical.appointments.create')"
        placement="left"
      >
        <va-button
          @click.prevent="createWithDefaults">
          <i class="fa fa-plus"></i>
        </va-button>

      </va-popover>

    </div>

    <Breadcrumbs :breadcrumbs="breadcrumbs">
      <template slot="breadcrumbAfter">
        <!-- User Details Action Dropdown -->
        <va-dropdown
          v-if="canUpdatePerson"
          class="custom-dropdown"
          fixed>
          <span slot="anchor">
            <slot>
              <div class="pl-3 pr-2 clickable">
                <span class="fa fa-ellipsis-v" style="font-size: 1.5rem;"></span>
              </div>
            </slot>
          </span>
          <div class="contact-dropdown__content">
            <div v-if="canUpdatePerson" class="contact-dropdown__item align--center" @click.prevent="isUpdatingPerson = true">
              <i class="fa fa-pencil"></i>&nbsp;
              <span class="pl-2">{{$t('people.actions.edit')}}</span>
            </div>
            <div v-if="canUpdatePerson" class="contact-dropdown__item align--center" @click.prevent="showDestroyPersonConfirmation = true">
              <i class="fa fa-trash"></i>&nbsp;
              <span class="pl-2">{{$t('people.actions.delete')}}</span>
            </div>
          </div>
        </va-dropdown>
        <!-- End User Details Actions Dropdown -->
      </template>
    </Breadcrumbs>

    <div v-if="person" class="mb-5">
      <person-info
        class="my-3"
        :person="person">
      </person-info>

      <appointment-list
        ref="scheduledAppointmentsList"
        class="mb-3"
        :title="$t('people.appointments_info.header')"
        :person="person"
        :service="component.service"
        :params="{startDate: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss')}"
        :options="{orderBy: {ascending: true}}"
        @row-click="onAppointmentSelected"
      >
      </appointment-list>

      <appointment-list
        ref="pastAppointmentsList"
        class="mb-3"
        :title="$t('people.appointments_history')"
        :person="person"
        :service="component.service"
        :params="{endDate: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss')}"
        :options="{orderBy: {ascending: false}}"
        @row-click="onAppointmentSelected"
      >
      </appointment-list>

      <section v-if="canReadPersonClinicalDetails" class="form-section">

        <h4 class="mb-3">{{$t('clinical.people.clinical_questionnaire')}}</h4>

        <ClinicalQuestionnaireForm
          :value="latestClinicalQuestionnaire"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(latestClinicalQuestionnaire)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </ClinicalQuestionnaireForm>

        <LifeStyleForm
          :value="latestLifeStyle"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(latestLifeStyle)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </LifeStyleForm>

        <OtoscopyForm
          :value="latestOtoscopy"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(latestOtoscopy)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </OtoscopyForm>

        <ImpedanceForm
          :value="latestImpedance"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(latestImpedance)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </ImpedanceForm>

        <TonalAudiometryForm
          :value="latestTonalAudiometry"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(latestTonalAudiometry)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </TonalAudiometryForm>

        <VocalAudiometryForm
          :value="latestVocalAudiometry"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="isOutdatedAnamnesisModel(latestVocalAudiometry)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </VocalAudiometryForm>

        <HearingDevicesForm
          :value="latestHearingDevices"
          :editing="false"
          :collapsed="true"
          :collapsable="true"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <va-badge class="outdated-data-warning-badge" v-if="areOutdatedHearingDevicesModels(latestHearingDevices)">
              {{$t('clinical.people.anamnesis.outdated_data_warning_message')}}
            </va-badge>
          </div>
        </HearingDevicesForm>

      </section>
    </div>

    <AppointmentForm
      v-if="showAppointmentForm"
      v-bind="propsAppointmentForm"
      :service="component.service"
      @modal-closed="appointmentModalClosed"
      @appointment-created="updateAppointmentList"
      @appointment-updated="updateAppointmentList"
      @appointment-deleted="updateAppointmentList" />

    <ConfirmationModal
      v-if="showDestroyPersonConfirmation"
      :body="$t('clinical.people.destroy_person_confirmation_message', {name: this.person.name})"
      @modal-confirmed="confirmDestroyPerson"
      @modal-canceled="showDestroyPersonConfirmation = false">
    </ConfirmationModal>

    <CreateOrUpdatePerson
      v-if="isUpdatingPerson"
      :service="component.service"
      :serviceSlug="serviceSlug"
      :person="person"
      @person-updated="onPersonUpdated"
      @cancel="onCancelPersonUpdate"
    >
    </CreateOrUpdatePerson>

    <AppointmentDetails
      v-if="showAppointmentInfo"
      :service="component.service"
      :appointment="selectedAppointment"
      :inactiveEntityActivityUsers="inactiveEntityActivityUsers"
      @appointment-updated="onAppointmentUpdated"
      @appointment-deleted="onAppointmentDeleted"
      @modal-closed="closeAppointmentInfo">
    </AppointmentDetails>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import PersonInfo from './PersonInfo'
import { uniqBy, flatten } from 'lodash'
import AppointmentList from './AppointmentList'
import { ActivityService } from '@/api/AuthService'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { PersonService } from '@/api/AuthClinicalService'
import AppointmentForm from '../../appointments/AppointmentForm'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { FORM_DEFAULTS } from '@/components/clinical/appointments/config'
import ClinicalQuestionnaireForm from '@/components/clinical/people/Anamnesis/ClinicalQuestionnaireForm'
import LifeStyleForm from '@/components/clinical/people/Anamnesis/LifeStyleForm'
import OtoscopyForm from '@/components/clinical/people/Anamnesis/OtoscopyForm'
import TonalAudiometryForm from '@/components/clinical/people/Anamnesis/TonalAudiometryForm'
import VocalAudiometryForm from '@/components/clinical/people/Anamnesis/VocalAudiometryForm'
import HearingDevicesForm from '@/components/clinical/people/Anamnesis/HearingDevicesForm'
import AppointmentDetails from '@/components/clinical/appointments/AppointmentDetails'
import CreateOrUpdatePerson from '@/components/clinical/people/CreateOrUpdatePerson'
import ImpedanceForm from "@/components/clinical/people/Anamnesis/ImpedanceForm";
import LocalStorageHelper from "@/local-storage/LocalStorageHelper";

const DispenserFieldId = 8
const DispenserPremisesId = 9
const EntityActivityUserInactiveStatus = 3

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  components: {
    ImpedanceForm,
    PersonInfo,
    Breadcrumbs,
    AppointmentList,
    AppointmentForm,
    ConfirmationModal,
    AppointmentDetails,
    ClinicalQuestionnaireForm,
    LifeStyleForm,
    OtoscopyForm,
    TonalAudiometryForm,
    VocalAudiometryForm,
    HearingDevicesForm,
    CreateOrUpdatePerson
  },
  data: function () {
    return {
      person: null,
      showAppointmentForm: false,
      showDestroyPersonConfirmation: false,
      propsAppointmentForm: {},
      showAppointmentInfo: false,
      selectedAppointment: null,
      writeEntityActivityUsers: [],
      isUpdatingPerson: false,
      inactiveEntityActivityUsers: []
    }
  },
  created: function () {
    this.loadEntityActivityUsers()

    const authClinicalServiceId = this.component.service.id
    if(this.$route.params.id !== null && this.$route.params.id !== undefined) {
      LocalStorageHelper.set(`auth_clinical_${authClinicalServiceId}_last_patient_page`, parseInt(this.$route.params.id))
    }
  },
  methods: {
    onPersonUpdated: function ({ person }) {
      this.person = person
    },
    onCancelPersonUpdate: function () {
      this.isUpdatingPerson = false
    },
    hasEntityActivityUserForPeriod: function (start, end) {
      return this.writeEntityActivityUsers.some(entityActivityUser => {
        let startDate = this.moment(entityActivityUser.start_date)
        let endDate = this.moment(entityActivityUser.end_date)
        return ((!startDate.isValid() || startDate.isSameOrBefore(start)) && (!endDate.isValid() || endDate.isSameOrAfter(end)))
      })
    },
    loadEntityActivityUsers: function () {
      let params = {
        start: this.moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end: this.moment().add(3, 'months').endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        infoType: this.authClinicalInfoTypes.APPOINTMENTS,
        accessType: this.accessTypes.WRITE,
        activities: `${DispenserFieldId},${DispenserPremisesId}`,
        serviceId: this.component.service.id,
        inclusive: true
      }
      ActivityService.getActivityUsersByEntity(params)
        .then(entityActivityUsers => {
          this.writeEntityActivityUsers = entityActivityUsers
          this.inactiveEntityActivityUsers = entityActivityUsers.filter(eau => eau.status_id === EntityActivityUserInactiveStatus)
          this.getFirstAvailableDates()
        }).catch(errors => {
          console.log(errors)
        })
    },
    onAppointmentSelected: function ({ appointment }) {
      this.selectedAppointment = appointment
      this.showAppointmentInfo = true
    },
    closeAppointmentInfo: function () {
      this.showAppointmentInfo = false
      this.selectedAppointment = null
    },
    onAppointmentUpdated: function ({ appointment }) {
      this.selectedAppointment = appointment
      this.refreshAppointmentTables()
    },
    onAppointmentDeleted: function () {
      this.selectedAppointment = null
      this.showAppointmentInfo = false
      this.refreshAppointmentTables()
    },
    onClickEditPerson: function () {
      this.$router.push({ name: `${this.serviceSlug}clinical/people/:id/edit`, params: { id: this.person.id } })
    },
    personUpdated: function ({ person }) {
      this.showToast(this.$t('people.actions.updated'), this.$store.state.successToastOptions)
      this.person = person
    },
    personDeleted: function () {
      this.showToast(this.$t('people.actions.deleted'), this.$store.state.successToastOptions)
      this.redirectBack()
    },
    redirectBack: function () {
      this.$router.go(-1)
    },
    selectPerson: function (personId) {
      PersonService.get(this.component.service.url, personId)
        .then(person => {
          this.person = person
        }).catch(errors => {
          console.log(errors)
          this.redirectBack()
        })
    },
    confirmDestroyPerson: function () {
      PersonService.destroy(this.component.service.url, this.person.id)
        .then(response => {
          this.showToast(this.$t('people.actions.deleted'), this.$store.state.successToastOptions)
          if (response.data.error) {
            setTimeout(() => {
              this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
            }, this.$store.state.errorToastOptions.duration)
          }
          const authClinicalServiceId = this.component.service.id
          LocalStorageHelper.remove(`auth_clinical_${authClinicalServiceId}_last_patient_page`)
          this.$router.push({ name: `${this.serviceSlug}clinical/people` })
        }).catch(errors => {
          console.log(errors)
          this.showToast(this.$t('people.actions.deleted_failed'), this.$store.state.errorToastOptions)
        })
    },
    updateAppointmentList: function ({ request }) {
      this.showAppointmentForm = false
      this.refreshAppointmentTables()
    },
    refreshAppointmentTables: function () {
      this.$refs.scheduledAppointmentsList.reloadTable()
      this.$refs.pastAppointmentsList.reloadTable()
    },
    getNewAppointmentDateDefaults: function () {
      let start = this.moment().add(FORM_DEFAULTS.NEXT_START_INCREMENT, FORM_DEFAULTS.NEXT_START_INTERVAL).startOf(FORM_DEFAULTS.NEXT_START_INTERVAL)
      let end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
      return {
        start,
        end,
      }
    },
    getFirstAvailableDates: function () {
      let startDate = this.writeEntityActivityUsers.map(entityActivityUser => {
        return this.moment(entityActivityUser.start_date)
      }).reduce(function (initialDate, startDate) {
        return startDate.isSameOrBefore(initialDate) ? startDate : initialDate
      })
      return {
        start: startDate,
        end: startDate.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
      }
    },
    createWithDefaults: function () {
      if (!this.facilitiesAdmin.length) return

      this.propsAppointmentForm = {
        facilities: this.facilitiesAdmin,
        person: this.person
      }

      let { start, end } = this.getNewAppointmentDateDefaults()

      if (!this.hasEntityActivityUserForPeriod(start, end)) {
        let { start, end } = this.getFirstAvailableDates()

        this.propsAppointmentForm.fromEvent = {
          start: start.format('YYYY-MM-DD HH:mm:ss'),
          end: end.format('YYYY-MM-DD HH:mm:ss')
        }
      }

      this.showAppointmentForm = true
    },
    appointmentModalClosed: function () {
      this.propsAppointmentForm = {}
      this.showAppointmentForm = false
    },
    isOutdatedAnamnesisModel: function (model) {
      return model !== null && this.person.last_appointment_date !== null && this.moment(model.updated_at).isBefore(this.moment(this.person.last_appointment_date))
    },
    areOutdatedHearingDevicesModels: function (hearingDevices) {
      if (Array.isArray(hearingDevices) && hearingDevices.length > 0 && this.person.last_appointment_date !== null) {
        return hearingDevices.every(hearingDevice => this.moment(hearingDevice.updated_at).isBefore(this.moment(this.person.last_appointment_date)))
      } else {
        return false
      }
    }
  },
  computed: {
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.component.service.slug}`
    },
    ...mapState({
      authClinicalInfoTypes: function (state) {
        return state[this.waitingRoomModuleKey].authClinicalInfoTypes
      },
      accessTypes: function (state) {
        return state[this.waitingRoomModuleKey].accessTypes
      }
    }),
    canUpdatePerson: function () {
      return this.person !== null && this.component.service.user_permissions.some(p => p.access_type >= this.accessTypes.WRITE && p.info_type === this.authClinicalInfoTypes.PEOPLE && (p.entity_id === null || p.entity_id === this.person.entity.id))
    },
    canReadPersonClinicalDetails: function () {
      console.log(this.accessTypes.READ, this.authClinicalInfoTypes.PEOPLE_CLINICAL_DETAILS)
      return this.person !== null && this.component.service.user_permissions.some(p => p.access_type >= this.accessTypes.READ && p.info_type === this.authClinicalInfoTypes.PEOPLE_CLINICAL_DETAILS && (p.entity_id === null || p.entity_id === this.person.entity.id))
    },
    breadcrumbs: function () {
      let items = [
        { title: this.$t(`menu.${this.serviceSlug}`), disabled: true },
        { title: this.$t('menu.clinical/people'), meta: { type: 'function', value: () => {
          this.$router.push(`/${this.serviceSlug}/clinical/people`)
              const authClinicalServiceId = this.component.service.id
              LocalStorageHelper.remove(`auth_clinical_${authClinicalServiceId}_last_patient_page`)
        } } } // eslint-disable-line
      ]
      if (this.person) {
        items.push({ title: this.personName, active: true })
      }
      return items
    },
    personName: function () {
      if (!this.person) {
        return null
      }
      return this.person.last_name ? `${this.person.first_name} ${this.person.last_name}` : this.person.first_name
    },
    facilitiesAdmin: function () {
      return uniqBy(flatten(this.writeEntityActivityUsers.map(entityActivityUser => {
        return entityActivityUser.entity.facilities
      })), 'id')
    },
    latestClinicalQuestionnaire: function () {
      return Array.isArray(this.person.clinical_questionnaires) && this.person.clinical_questionnaires.length > 0 ? this.person.clinical_questionnaires[0] : null
    },
    latestLifeStyle: function () {
      return Array.isArray(this.person.life_styles) && this.person.life_styles.length > 0 ? this.person.life_styles[0] : null
    },
    latestOtoscopy: function () {
      return Array.isArray(this.person.otoscopies) && this.person.otoscopies.length > 0 ? this.person.otoscopies[0] : null
    },
    latestImpedance: function () {
      return Array.isArray(this.person.impedances) && this.person.impedances.length > 0 ? this.person.impedances[0] : null
    },
    latestVocalAudiometry: function () {
      return Array.isArray(this.person.vocal_audiometries) && this.person.vocal_audiometries.length > 0 ? this.person.vocal_audiometries[0] : null
    },
    latestTonalAudiometry: function () {
      return Array.isArray(this.person.tonal_audiometries) && this.person.tonal_audiometries.length > 0 ? this.person.tonal_audiometries[0] : null
    },
    latestHearingDevices: function () {
      return Array.isArray(this.person.hearing_devices) && this.person.hearing_devices.slice(0, 2)
    }
  },
  beforeRouteEnter (to, from, next) {
    if (to.params.id !== undefined) {
      next(vm => {
        vm.selectPerson(parseInt(to.params.id))
        vm.from = from
      })
    } else {
      next(from)
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params.id !== undefined) {
      this.selectPerson(parseInt(to.params.id))
    } else {
      next(from)
    }
  }
}
</script>

<style lang="scss">

.va-dropdown__content {
  left: -40px !important;
  top: -30px !important;
}

#person-details-page {
  .contact-dropdown__item {
    &:first-of-type {
      padding-top: 0.8rem !important;
      padding-bottom: 0.8rem !important;
      padding-left: 0.8rem !important;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
    &:last-of-type {
      padding-top: 0.8rem !important;
      padding-bottom: 0.8rem !important;
      padding-left: 0.8rem !important;
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
    i {
      color: #29c4aa;
    }
    &:hover {
      background-color: #29c4aa;
      * {
        color: white;
      }
    }
  }
}

</style>
